import { datadogRum } from "@datadog/browser-rum"

// RUM へ送る URL から redact する sensitive query param。
// code/state: Auth0 callback の authorization code / CSRF state。
// linkToken/token: LINE 連携系 one-time token。
const SENSITIVE_QUERY_PARAMS = ["code", "state", "linkToken", "token"]

// 自由文(error.message / stack)中に埋め込まれた http(s) URL を抽出するパターン。
const URL_PATTERN = /https?:\/\/[^\s"')]+/g

// URL 文字列中の sensitive query param / fragment 値を redact する。
export function scrubUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin)
    let changed = false
    for (const key of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, "REDACTED")
        changed = true
      }
    }
    // fragment(#...)も Auth0 implicit / LIFF が key=value でトークンを載せ得る。
    // sensitive key を含む場合は fragment 全体を落とす(fragment 文法は非標準のため部分置換しない)。
    if (parsed.hash) {
      const fragParams = new URLSearchParams(parsed.hash.replace(/^#/, ""))
      if (SENSITIVE_QUERY_PARAMS.some((key) => fragParams.has(key))) {
        parsed.hash = ""
        changed = true
      }
    }
    return changed ? parsed.toString() : url
  } catch {
    return url
  }
}

// 自由文中に埋め込まれた URL を scrubUrl で redact する(error.message / stack 用)。
export function scrubText(text: string): string {
  return text.replace(URL_PATTERN, (match) => scrubUrl(match))
}

// Datadog RUM を初期化する。applicationId / clientToken が未設定(本番以外)なら no-op。
export function initDatadog(): void {
  const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN
  if (!applicationId || !clientToken) {
    return
  }

  const apiUrl = process.env.REACT_APP_API_URL

  datadogRum.init({
    applicationId,
    clientToken,
    site: "ap1.datadoghq.com",
    service: process.env.REACT_APP_DATADOG_SERVICE || "triple-talent-frontend",
    env: process.env.REACT_APP_DATADOG_ENV || process.env.NODE_ENV,
    version: process.env.REACT_APP_DATADOG_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask",
    enablePrivacyForActionName: true,
    ...(apiUrl ? { allowedTracingUrls: [apiUrl], traceSampleRate: 100 } : {}),
    beforeSend: (event) => {
      if (event.view?.url) {
        event.view.url = scrubUrl(event.view.url)
      }
      if (event.view?.referrer) {
        event.view.referrer = scrubUrl(event.view.referrer)
      }
      if (event.type === "resource") {
        event.resource.url = scrubUrl(event.resource.url)
      }
      if (event.type === "error") {
        if (event.error.resource) {
          event.error.resource.url = scrubUrl(event.error.resource.url)
        }
        if (event.error.message) {
          event.error.message = scrubText(event.error.message)
        }
        if (event.error.stack) {
          event.error.stack = scrubText(event.error.stack)
        }
      }
      return true
    },
  })
}

// RUM セッションにログインユーザーを紐付ける。PII 規約により id のみ。SDK 未 init では no-op。
export function setDatadogUser(userId: number | string): void {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.setUser({ id: String(userId) })
  }
}

// RUM セッションのユーザー紐付けを解除する。SDK 未 init では no-op。
export function clearDatadogUser(): void {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.clearUser()
  }
}
